import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { LOGOUT } from '../../constants/actions/auth.constants';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import QuestionnaireActions from '../../participant/actions/QuestionnaireActions';
import UserService from '../services/UserService';
import IBaseState from '../../types/IBaseState';
import UserInfo from '../types/UserInfo';
import UserActions from '../../actions/UserActions';
import DemographicActions from '../actions/DemographicActions';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';

const NAME = 'User';

export type UserState = {
    activeQuestionnaires: IBaseState<unknown[]>;
    info: IBaseState<UserInfo | undefined>;
};

const initialState: UserState = {
    activeQuestionnaires: {
        data: [],
        loading: false,
    },
    info: {
        data: undefined,
        loading: false,
    },
};

const getActiveQuestionnaires = createAsyncThunk(
    `${NAME}/getActiveQuestionnaires`,
    UserService.getActiveQuestionnaires,
    {
        condition: (_arg, { getState }) => {
            const aq = (getState() as AppState).common.user
                .activeQuestionnaires;
            return !aq.loading;
        },
    },
);

const getInfo = createNotifiedThunk(
    `${NAME}/getInfo`,
    UserService.getInfo,
    { error: 'Nepodařilo se načíst info uživatele' },
    {
        condition: (_arg, { getState }) => {
            const info = (getState() as AppState).common.user.info;
            return !info.loading;
        },
    },
);

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getActiveQuestionnaires.pending, (state: UserState) => {
            if (state.activeQuestionnaires.loading === false) {
                state.activeQuestionnaires.loading = true;
            }
        });
        builder.addCase(
            getActiveQuestionnaires.fulfilled,
            (state: UserState, action) => {
                if (state.activeQuestionnaires.loading === true) {
                    state.activeQuestionnaires.data =
                        (action.payload as { data?: unknown[] })?.data || [];
                }
            },
        );
        builder.addCase(
            QuestionnaireActions.closeQuestionnaire,
            (state: UserState, action) => {
                if (state.activeQuestionnaires.data !== null) {
                    const data = state.activeQuestionnaires.data as {
                        id?: unknown;
                    }[];
                    const payload = action.payload as
                        { id?: unknown } | undefined;
                    const targetId = payload?.id;
                    const index = data.findIndex(
                        (item) => item.id === targetId,
                    );

                    if (index >= 0) {
                        data.splice(index, 1);
                    }
                }
            },
        );

        builder.addCase(getInfo.pending, (state: UserState) => {
            if (state.info.loading === false) {
                state.info.loading = true;
            }
            state.info.error = false;
        });
        // A load skipped by the condition dispatches no rejected action, so this is a real failure.
        builder.addCase(getInfo.rejected, (state: UserState) => {
            state.info.error = true;
        });
        builder.addCase(getInfo.fulfilled, (state: UserState, action) => {
            if (state.info.loading === true) {
                state.info.data = (action.payload as { data?: UserInfo })?.data;
            }
        });
        builder.addCase(
            UserActions.changePassword.fulfilled,
            (state: UserState) => {
                if (state.info.data) {
                    state.info.data.forceChangePassword = false;
                }
            },
        );

        builder.addCase(DemographicActions.postAnswer.fulfilled, (state) => {
            if (state.info.data) {
                state.info.data.showDemographicCriteria = false;
            }
        });

        builder.addCase(LOGOUT, () => initialState);

        builder.addMatcher(
            isAnyOf(
                getActiveQuestionnaires.fulfilled,
                getActiveQuestionnaires.rejected,
            ),
            (state: UserState) => {
                if (state.activeQuestionnaires.loading === true) {
                    state.activeQuestionnaires.loading = false;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getInfo.fulfilled, getInfo.rejected),
            (state: UserState) => {
                if (state.info.loading === true) {
                    state.info.loading = false;
                }
            },
        );
    },
});

const state = (state: AppState) => state.common.user;

const UserSlice = {
    actions: { getActiveQuestionnaires, getInfo },
    reducer: slice.reducer,
    state: {
        activeQuestionnaires: createSelector(
            state,
            (state: UserState) => state.activeQuestionnaires,
        ),
        activeQuestionnaire: createSelector(state, (state: UserState) =>
            (state.activeQuestionnaires.data?.length ?? 0) > 0
                ? state.activeQuestionnaires.data[0]
                : null,
        ),
        info: createSelector(state, (state: UserState) => state.info),
    },
};

export default UserSlice;
